export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || '/api/v1',
  TIMEOUT: 30000,
  // Timeout for connect-flow `complete-setup` calls (Google + Meta).
  // The backend blocks on Cloud Run with `wait_for_sync=True`, which has
  // its own 300s cap. The Meta side additionally hard-deletes the
  // credential and returns success=false on aggregate-spend gate
  // rejection — client must stay awake for the full server window or the
  // "timeout as success" fallback (see MetaOAuthCallback + Google's
  // PollingManager) can flash a linked UI while the credential was
  // silently deleted server-side. 330s = server 300s + 30s network/proc
  // buffer, keeping us safely past the backend deadline. Other long-
  // running endpoints (image uploads, ad-group creation, asset
  // mutations) still inline `180000` literals — sweep into this constant
  // in a follow-up rather than expanding scope here.
  COMPLETE_SETUP_TIMEOUT_MS: 330000,
} as const;

export const CACHE_CONFIG = {
  DEFAULT_DURATION: 5 * 60 * 1000, // 5 minutes
  MAX_ENTRIES: 100,
} as const;

export const POLLING_CONFIG = {
  FAST_INTERVAL: 4000, // 4 seconds
  FAST_ATTEMPTS: 20,   // 80 seconds total
  SLOW_INTERVAL: 20000, // 20 seconds
  SLOW_ATTEMPTS: 24,    // 8 minutes total
  INITIAL_DELAY: 6000,  // 6 seconds before first poll
} as const;

export const EMAIL_VERIFICATION_POLL = {
  INTERVAL_MS: 3000,
  DEADLINE_MS: 10 * 60 * 1000, // 10 minutes
} as const;

export const ROUTES = {
  LOGIN: '/login',
  HOME: '/',
  ACCOUNT: '/account',
  CAMPAIGN: '/google-campaign/:id',
  NOT_FOUND: '*',
} as const;

export const SESSION_KEYS = {
  AUTH_TOKEN: 'authToken',
  MEMBER_UUID: 'memberUuid',
  CACHE_PREFIX: 'cache_',
  CACHE_TIMESTAMP_SUFFIX: '_ts',
  META_OAUTH_STATE: 'oauth_state_meta',
} as const;

export const BUDGET_CONFIG = {
  // Average days per standard time span for budget estimation
  // Currently represents average days per month (365.25 / 12 = 30.4375, rounded to 30.4)
  // Can be adjusted for different time spans (e.g., 91.25 for quarterly)
  AVERAGE_DAYS_PER_STANDARD_TIME_SPAN: 30.4,
  // Display label for the time span (e.g., "Monthly", "Quarterly")
  TIME_SPAN_LABEL: 'monthly',
} as const;

// Tier threshold settings - must match backend config.py defaults
// TIER_STANDARD_MIN_SPEND_PER_ACCOUNT_30DAYS and TIER_SEASONAL_MIN_SPEND_PER_ACCOUNT_YEAR
export const TIER_CONFIG = {
  STANDARD_MIN_SPEND_PER_ACCOUNT_30DAYS: 25,
  SEASONAL_MIN_SPEND_PER_ACCOUNT_YEAR: 300,
} as const;

// ROAS calculation settings
export const ROAS_CONFIG = {
  // Default revenue per click used when user hasn't set a custom value
  // This is a conservative default to avoid overstating ROAS
  DEFAULT_REVENUE_PER_CLICK: 0.1,
} as const;